import React, { useEffect, useState } from "react";
import {
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Link,
  Button,
  useDisclosure,
  Text,
  Box,
} from "@chakra-ui/react";
import LogModal from "./LogModal";
import dayjs from "dayjs";
import { common, button } from "../../constants/constants";
import { TABLE_STYLES } from "./FileTable.styles";

export default function FileTable({
  refreshKey,
  fetchFiles,
  loading,
  data,
  columns,
  searchText,
  currentPage,
  onRefresh,
  handleCommit,
  handleAlgoliaTrigger,
  customActionHandler
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedLog, setSelectedLog] = useState(null);
  const [status,setStatus]=useState(null)

  const handleViewLog = (log,status) => {
    setSelectedLog(log);
    setStatus(status)
    onOpen();
  };

  const handleAlgoliaClick = (row) => {
    const { _id, file_name, status } = row;
    let type = '';
    
    if (status === 'Done archive') {
      type = 'archive';
    } else if (status === 'Done library') {
      type = 'library';
    }
    
    handleAlgoliaTrigger(_id, file_name, type);
  };

  const shouldShowAlgoliaButton = (row) => {
    return row.type === 'algolia' && 
           row.status && 
           row.status.includes('Done') && 
           row.file_name;
  };

  useEffect(() => {
    fetchFiles(currentPage,searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey,currentPage]);

  if (loading) {
    return (
      <TableContainer {...TABLE_STYLES.container}>
        <Table {...TABLE_STYLES.table}>
          <Tbody>
            <Tr>
              <Td colSpan={columns?.length || 3} {...TABLE_STYLES.loadingTd}>
                <Box {...TABLE_STYLES.loadingBox}>
                  <Spinner size="lg" color="blue.500" thickness="3px" />
                  <Text color="gray.500" fontSize="14px" fontWeight="400">
                    Loading data...
                  </Text>
                </Box>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <>
      <TableContainer {...TABLE_STYLES.container}>
        <Table {...TABLE_STYLES.table}>
          <Thead {...TABLE_STYLES.thead}>
            <Tr>
              {columns?.map((col) => (
                <Th key={col?.key} {...TABLE_STYLES.th}>
                  {col?.label}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {data && data?.length>0 && data?.map((row, rowIndex) => (
              <Tr key={row?.id || rowIndex} {...TABLE_STYLES.tr}>
                {columns?.map((col) => {
                  const value = row[col?.key];
                  const status=row['status']
                  if (col?.key === "file_name") {
                    return (
                      <Td key={col?.key} {...TABLE_STYLES.td}>
                      {row['s3_url'] || row['derived_s3_url'] ? (
                        <Link href={row['s3_url'] || row['derived_s3_url']} isExternal color="blue.500">
                          {value}
                        </Link>
                      ) : (
                        <Text>
                          {value}
                        </Text>
                      )}
                    </Td>
                  );
                }

                if (col?.key === "log") {
                  return (
                    <Td key={col?.key} {...TABLE_STYLES.td}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewLog(value,status)}
                      >
                        {common.VIEW}
                      </Button>
                    </Td>
                  );
                }

                if (col?.key === "derived_s3_url") {
                  return (
                    <Td key={col?.key} {...TABLE_STYLES.td}>
                      {value ? (
                        <Link href={value} isExternal color="blue.500">
                          {common.DOWNLOAD}
                        </Link>
                      ) : (
                        <Text color="gray.400" fontStyle="italic">
                          {common.NOT_AVAILABLE}
                        </Text>
                      )}
                    </Td>
                  );
                }
                if (col?.key === "error_file") {
                  return (
                    <Td key={col?.key} {...TABLE_STYLES.td}>
                      {value ? (
                        <Link href={value} isExternal color="blue.500">
                          {common.DOWNLOAD}
                        </Link>
                      ) : (
                        <Text color="gray.400" fontStyle="italic">
                          {common.NOT_AVAILABLE}
                        </Text>
                      )}
                    </Td>
                  );
                }

                if (col?.key === "s3_url" && row['type'] === 'excel_datas') {
                  return (
                    <Td key={col?.key} {...TABLE_STYLES.td}>
                      {value ? (
                        <Link href={value} isExternal color="blue.500">
                          {common.DOWNLOAD}
                        </Link>
                      ) : (
                        <Text color="gray.400" fontStyle="italic">
                          {common.NOT_AVAILABLE}
                        </Text>
                      )}
                    </Td>
                  );
                }
                  if (col?.key === "type") {
                  return (
                    <Td key={col?.key} {...TABLE_STYLES.td}>
                      {value==="commit" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCommit(row['_id'])}
                          isDisabled={row['status'] !== 'new'}
                          opacity={row['status'] !== 'new' ? 0.6 : 1}
                          cursor={row['status'] !== 'new' ? 'not-allowed' : 'pointer'}
                        >
                          {value}
                        </Button>
                      ) : (
                        <Text color="gray.400" fontStyle="italic">
                          {value}
                        </Text>
                      )}
                    </Td>
                  );
                }

                if (col?.key === "status") {
                  return (
                    <Td key={col?.key} {...TABLE_STYLES.td}>
                      {shouldShowAlgoliaButton(row) ? (
                        <Button
                          size="sm"
                          variant="outline"
                          colorScheme="blue"
                          onClick={() => handleAlgoliaClick(row)}
                        >
                          Algolia Trigger
                        </Button>
                      ) : (
                        <Text>
                          {value || "--"}
                        </Text>
                      )}
                    </Td>
                  );
                }

                if (col?.key === "created_at") {
                  return (
                    <Td key={col?.key} {...TABLE_STYLES.td}>
                      {value
                        ? dayjs(value).format("DD MMM YYYY, hh:mm A")
                        : "--"}
                    </Td>
                  );
                }

                if (col?.key === "action" && customActionHandler) {
                  return (
                    <Td key={col?.key} {...TABLE_STYLES.td}>
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        onClick={() => customActionHandler(row)}
                        isDisabled={row.enabled === false}
                      >
                        {button.RUN_NOW}
                      </Button>
                    </Td>
                  );
                }

                  return (
                    <Td key={col?.key} {...TABLE_STYLES.td}>
                      {value || "--"}
                    </Td>
                  );
                })}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      <LogModal isOpen={isOpen} onClose={onClose} logData={selectedLog} onRefresh ={onRefresh} status={status}/>
    </>
  );
}
